import type { SlideFields } from "@/types/Slide";

export interface SlideReorderAnchors {
  /** sortOrder of the slide the moved slide should follow, null when it lands first. */
  prevSortOrder: number | null;
  /** Midpoint to persist, null when there is no following slide and the API picks the value. */
  newSortOrder: number | null;
}

/**
 * Picks the sortOrder gap a dragged slide should land in.
 *
 * The lesson view renders one sortable list per tag section, so the two slides framing a
 * drop inside a section are usually not adjacent in the lesson's global order. Their
 * midpoint can therefore equal the sortOrder another section's slide already holds, and the
 * API resolves that tie by renumbering the whole lesson. Anchoring to the globally adjacent
 * pair around the same drop point keeps the target gap free while landing the slide in the
 * same place within its section.
 *
 * `lessonSlides` must be the full list the sections were built from, ordered by sortOrder.
 */
export function getSlideReorderAnchors({
  movedSlideId,
  sectionPrev,
  sectionNext,
  lessonSlides,
}: {
  movedSlideId: string;
  sectionPrev: SlideFields | undefined;
  sectionNext: SlideFields | undefined;
  lessonSlides: SlideFields[];
}): SlideReorderAnchors {
  const otherSlides = lessonSlides.filter((slide) => slide.id !== movedSlideId);

  // Dropped at the head of its section: anchor to whatever precedes the section's next slide.
  const anchorIndex = sectionPrev
    ? otherSlides.findIndex((slide) => slide.id === sectionPrev.id)
    : otherSlides.findIndex((slide) => slide.id === sectionNext?.id) - 1;

  const prev = otherSlides[anchorIndex];
  const next = otherSlides[anchorIndex + 1];
  const prevSortOrder = prev?.sortOrder ?? null;

  return {
    prevSortOrder,
    newSortOrder: next != null ? ((prevSortOrder ?? 0) + next.sortOrder) / 2 : null,
  };
}
